import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import NavbarAr from "@/components/NavbarAr";
import FooterAr from "@/components/FooterAr";
import Brand from "@/components/Brand";
import { Mail, Instagram, Youtube, ExternalLink, CheckCircle2, ArrowUpRight, ShieldAlert } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { EMAILS, SOCIAL } from "@/lib/brand";

// Contact form uses a bare axios POST (no `withCredentials`) so Safari
// does not reject the response under the CORS rule that forbids
// credentialed requests against an `Access-Control-Allow-Origin: *`
// backend. This endpoint is public — no cookies or auth headers needed.
const CONTACT_ENDPOINT = `${process.env.REACT_APP_BACKEND_URL}/api/contact`;

const input =
    "w-full bg-[var(--crewz-surface-1)] border border-white/10 focus:border-[var(--crewz-gold)] focus:outline-none text-white px-4 py-3 font-body text-sm placeholder:text-zinc-600 transition-colors";

// General Inquiry + the 5 membership categories, matching Membership.jsx CATEGORIES
const REASONS_EN = [
    { v: "general", l: "General Inquiry" },
    { v: "car_owner", l: "Car Owner — Membership" },
    { v: "motorcycle_owner", l: "Motorcycle Owner — Membership" },
    { v: "media", l: "Media — Membership" },
    { v: "brand", l: "Brand / Sponsor — Membership" },
    { v: "guest", l: "Guest — Membership" },
];

const REASONS_AR = [
    { v: "general", l: "استفسار عام" },
    { v: "car_owner", l: "عضوية مالك سيارة" },
    { v: "motorcycle_owner", l: "عضوية مالك دراجة نارية" },
    { v: "media", l: "عضوية إعلامي" },
    { v: "brand", l: "عضوية علامة تجارية / راعٍ" },
    { v: "guest", l: "عضوية زائر" },
];

export default function Contact({ locale = "en" }) {
    // Contact page — APPROVED · governance-refined (Feb 2026).
    //
    // Governing responsibility: provide a clear, trusted, and reliable
    // path for communicating with CrewZNatioN. This page's job is not
    // to carry constitutional philosophy — that belongs to About,
    // Membership, and the Constitution. Contact's greatest strength
    // is clarity, and its H1 is the plainest institutional expression
    // of what the page exists to do.
    //
    // See PRD §13.7 (Contact governance lock) and §17 (Ecosystem
    // Milestones) for full context.
    //
    // ─────────────────────────────────────────────────────────────
    // APPROVED STATE — do not modify without explicit owner
    // re-authorization:
    //
    //   • Header overline: "The open door." — sentence case with
    //     period, gold-rule prefix. Institutional posture.
    //   • H1: "This is how to reach CrewZNatioN." — declarative,
    //     six words, sentence case, single color, font-display,
    //     text-5xl md:text-6xl, max-w-3xl, leading-[0.98]. No
    //     uppercase spans, no gold accent. Aligned with locked-set
    //     editorial heading system.
    //   • Intro paragraph: "General inquiries and membership
    //     applications are both received here." — single-sentence
    //     factual, imperatives retired.
    //   • Interface labels (Primary Contact, Additional, Follow,
    //     Send a Message, Reason For Contact) intentionally
    //     UNCHANGED — editorial vs. interface distinction per
    //     PRD §13.7 (Round 2 owner refinement).
    //   • Gold submit button UNCHANGED — functional CTA, per
    //     PRD §13.7 (Round 2 owner rejection of restraint).
    //   • Success-state design UNCHANGED.
    //   • Reason-based routing (general vs. membership) UNCHANGED.
    //   • Backend /api/contact contract UNCHANGED.
    // ─────────────────────────────────────────────────────────────
    const navigate = useNavigate();
    const location = useLocation();
    const isAr = locale === "ar";
    const REASONS = isAr ? REASONS_AR : REASONS_EN;
    // Read `?reason=` query param on mount so cross-page CTAs (e.g.
    // Sponsors → "Begin the conversation") can land the visitor on
    // Contact with the correct enquiry bucket preselected. Only
    // values present in the REASONS list are accepted; anything
    // else falls back to "general". Setting the initial useState
    // value directly (rather than calling handleReasonChange) means
    // the redirect side-effect used for interactive membership picks
    // does NOT fire on this deep-link entry, so the visitor stays
    // on Contact and can submit the enquiry through /api/contact.
    const initialReason = (() => {
        const params = new URLSearchParams(location.search);
        const q = (params.get("reason") || "").toLowerCase();
        // Accept "sponsor" / "sponsorship" as aliases of "brand".
        const alias = q === "sponsor" || q === "sponsorship" ? "brand" : q;
        return REASONS.some((r) => r.v === alias) ? alias : "general";
    })();
    const [reason, setReason] = useState(initialReason);
    const [form, setForm] = useState({
        name: "",
        email: "",
        subject: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    // App Store / Play Console requirement: /contact must render a
    // clear support surface for the CrewZNatioN mobile application,
    // with a meaningful <title>, description and canonical URL.
    // Restores previous head values on unmount.
    useEffect(() => {
        const metaTitle = isAr
            ? "الدعم والتواصل — CrewZNatioN"
            : "Support & Contact — CrewZNatioN";
        const metaDesc = isAr
            ? "دعم تطبيق CrewZNatioN: الوصول إلى الحساب، وطلبات الخصوصية والحذف، والسلامة والإبلاغ، والفعاليات والتصاريح، والجراج والسوق، والدعم التقني."
            : "CrewZNatioN app support: account access, privacy and deletion requests, safety and reporting, events and passes, Garage and marketplace, and technical issues.";
        const canonicalUrl = isAr
            ? "https://www.crewznation.com/ar/contact"
            : "https://www.crewznation.com/contact";

        const prevTitle = document.title;
        document.title = metaTitle;
        let m = document.querySelector('meta[name="description"]');
        const prevDesc = m ? m.getAttribute("content") : null;
        if (!m) {
            m = document.createElement("meta");
            m.setAttribute("name", "description");
            document.head.appendChild(m);
        }
        m.setAttribute("content", metaDesc);

        let c = document.querySelector('link[rel="canonical"]');
        const prevCanonical = c ? c.getAttribute("href") : null;
        if (!c) {
            c = document.createElement("link");
            c.setAttribute("rel", "canonical");
            document.head.appendChild(c);
        }
        c.setAttribute("href", canonicalUrl);

        return () => {
            document.title = prevTitle;
            if (m && prevDesc !== null) m.setAttribute("content", prevDesc);
            if (c) {
                if (prevCanonical !== null) c.setAttribute("href", prevCanonical);
                else c.parentNode?.removeChild(c);
            }
        };
    }, [isAr]);

    const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const handleReasonChange = (e) => {
        const next = e.target.value;
        setReason(next);
        // Any membership category → route to full application form with category pre-selected
        if (next !== "general") {
            navigate(`${isAr ? "/ar/membership" : "/membership"}?category=${encodeURIComponent(next)}`);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            // Include the reason so admin knows what bucket this belongs to,
            // and the page language so admin can identify locale-of-origin.
            await axios.post(
                CONTACT_ENDPOINT,
                { ...form, reason, language: isAr ? "ar" : "en" },
                { headers: { "Content-Type": "application/json" } }
            );
            setDone(true);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const Nav = isAr ? NavbarAr : Navbar;
    const PageFooter = isAr ? FooterAr : Footer;

    return (
        <div dir={isAr ? "rtl" : "ltr"} className="min-h-screen bg-[var(--crewz-bg)]" data-testid="contact-page">
            <Nav />

            <section className="px-6 md:px-12 lg:px-20 pt-20 pb-12 max-w-5xl mx-auto">
                <div className="flex items-center gap-3 mb-5">
                    <span className="gold-rule" />
                    <p className="overline overline-primary">{isAr ? "باب التواصل" : "The open door."}</p>
                </div>
                <h1 className={`font-display text-5xl md:text-6xl text-[var(--crewz-text)] leading-[0.98] max-w-3xl${isAr ? " !leading-[60px] md:!leading-[72px]" : ""}`} data-testid="contact-title">
                    {isAr ? (
                        <>تواصل مع{" "}<span className="block md:inline mt-[6px] md:mt-0"><Brand /></span></>
                    ) : (
                        <>Reach <Brand />.</>
                    )}
                </h1>
                <p className="font-body text-lg md:text-xl text-[var(--crewz-text)]/85 mt-8 max-w-3xl font-light leading-relaxed">
                    {isAr
                        ? "تستقبل هذه الصفحة الاستفسارات العامة وطلبات العضوية."
                        : "General inquiries and membership applications are both received here."}
                </p>
            </section>

            <section className="px-6 md:px-12 lg:px-20 pb-16 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <p className="overline mb-5 text-zinc-500">{isAr ? "جهة التواصل الرئيسية" : "Primary Contact"}</p>
                    <a
                        href={`mailto:${EMAILS.primary}`}
                        className="font-display text-2xl md:text-3xl text-[var(--crewz-text)] hover:text-[var(--crewz-gold)] inline-flex items-center gap-3 transition-colors"
                        data-testid="contact-primary-email"
                        dir="ltr"
                    >
                        <Mail size={20} /> {EMAILS.primary}
                    </a>

                    <p className="overline mt-12 mb-5 text-zinc-500">{isAr ? "وسائل تواصل إضافية" : "Additional"}</p>
                    <ul className="font-body text-zinc-300 space-y-3">
                        <li>
                            <a
                                href={`mailto:${EMAILS.admin}`}
                                className="inline-flex items-center gap-2 hover:text-[var(--crewz-gold)] transition-colors"
                                dir="ltr"
                            >
                                <Mail size={14} /> {EMAILS.admin}
                            </a>
                        </li>
                        <li>
                            <a
                                href={`mailto:${EMAILS.secondary}`}
                                className="inline-flex items-center gap-2 hover:text-[var(--crewz-gold)] transition-colors"
                                dir="ltr"
                            >
                                <Mail size={14} /> {EMAILS.secondary}
                            </a>
                        </li>
                    </ul>

                    <p className="overline mt-12 mb-5 text-zinc-500">{isAr ? "تابعنا" : "Follow"}</p>
                    <div className="flex items-center gap-5 text-zinc-300">
                        <a
                            href={SOCIAL.instagram}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:text-[var(--crewz-gold)] transition-colors inline-flex items-center gap-2 font-body text-sm"
                            dir="ltr"
                            aria-label="Instagram @CrewZNatioN"
                            data-testid="contact-instagram"
                        >
                            <Instagram size={16} />
                            <strong dir="ltr" style={{ fontWeight: 800 }}>@CrewZNatioN</strong>
                        </a>
                        <a
                            href={SOCIAL.youtube}
                            target="_blank"
                            rel="noreferrer"
                            className="hover:text-[var(--crewz-gold)] transition-colors inline-flex items-center gap-2 font-body text-sm"
                            dir="ltr"
                            aria-label="YouTube @CrewZNatioN"
                            data-testid="contact-youtube"
                        >
                            <Youtube size={16} />
                            <strong dir="ltr" style={{ fontWeight: 800 }}>@CrewZNatioN</strong>
                        </a>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <p className="overline mb-5 text-zinc-500">{isAr ? "أرسل رسالة" : "Send a Message"}</p>
                    {done ? (
                        <div className="border border-white/10 bg-[var(--crewz-surface-1)] p-10" data-testid="contact-success">
                            <CheckCircle2 className="text-[var(--crewz-gold)]" size={40} />
                            <p className="font-display text-3xl text-white mt-4">
                                {isAr ? "تم استلام الرسالة." : "Message Received."}
                            </p>
                            <p className="font-body text-zinc-300 mt-3 leading-relaxed font-light">
                                {isAr ? (
                                    <>شكرًا لتواصلك مع <Brand />. سيراجع فريقنا رسالتك ويرد على البريد الإلكتروني الذي قدمته.</>
                                ) : (
                                    <>Thank you for reaching out to <Brand />. Our team will review your message and reply to the email you provided.</>
                                )}
                            </p>
                        </div>
                    ) : (
                        <form
                            onSubmit={submit}
                            className="bg-[var(--crewz-surface-1)] border border-white/10 p-8 md:p-10 space-y-5"
                            data-testid="contact-form"
                        >
                            <label className="block">
                                <span className="overline mb-2 block text-zinc-500">
                                    {isAr ? "سبب التواصل" : "Reason For Contact"} <span className="text-[var(--crewz-gold)]">*</span>
                                </span>
                                <div className="relative">
                                    <select
                                        required
                                        value={reason}
                                        onChange={handleReasonChange}
                                        className={`${input} appearance-none pr-10 cursor-pointer`}
                                        data-testid="contact-reason-select"
                                    >
                                        {REASONS.map((r) => (
                                            <option key={r.v} value={r.v} className="bg-[var(--crewz-surface-1)]">
                                                {r.l}
                                            </option>
                                        ))}
                                    </select>
                                    <ArrowUpRight
                                        size={16}
                                        className={`absolute ${isAr ? "left-3" : "right-3"} top-1/2 -translate-y-1/2 text-zinc-500 rotate-90 pointer-events-none`}
                                    />
                                </div>
                                <span className="font-body text-xs text-zinc-500 mt-2 block leading-relaxed">
                                    {isAr
                                        ? "اختر \"استفسار عام\" لأي استفسار لا يتعلق بالعضوية. أما عند اختيار إحدى فئات العضوية، فسيتم توجيهك إلى نموذج طلب العضوية الكامل."
                                        : "Choose \u201CGeneral Inquiry\u201D for any non-membership question. Selecting a membership category will take you to the full application form."}
                                </span>
                            </label>

                            {reason === "general" && (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <input
                                            type="text"
                                            required
                                            placeholder={isAr ? "الاسم" : "Your name"}
                                            value={form.name}
                                            onChange={update("name")}
                                            className={input}
                                            data-testid="contact-name-input"
                                        />
                                        <input
                                            type="email"
                                            required
                                            placeholder={isAr ? "البريد الإلكتروني" : "Email"}
                                            value={form.email}
                                            onChange={update("email")}
                                            className={input}
                                            data-testid="contact-email-input"
                                            dir="ltr"
                                        />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder={isAr ? "الموضوع" : "Subject"}
                                        value={form.subject}
                                        onChange={update("subject")}
                                        className={input}
                                        data-testid="contact-subject-input"
                                    />
                                    <textarea
                                        required
                                        rows={6}
                                        placeholder={isAr ? "اكتب رسالتك" : "Your message\u2026"}
                                        value={form.message}
                                        onChange={update("message")}
                                        className={`${input} resize-none`}
                                        data-testid="contact-message-input"
                                    />
                                    {error && (
                                        <p className="text-sm text-red-400 font-body" data-testid="contact-error">
                                            {error}
                                        </p>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className={`inline-flex items-center gap-2 bg-[var(--crewz-gold)] text-[var(--crewz-bg)] hover:bg-[var(--crewz-gold-soft)] disabled:opacity-60 px-7 py-4 text-sm tracking-[0.22em] ${isAr ? "" : "uppercase"} font-semibold transition-colors`}
                                        data-testid="contact-submit-btn"
                                    >
                                        {submitting
                                            ? (isAr ? "جاري الإرسال…" : "Sending\u2026")
                                            : (isAr ? "إرسال الرسالة" : "Send Message")}
                                    </button>
                                </>
                            )}
                        </form>
                    )}
                </div>
            </section>

            {/* CrewZNatioN app support — App Store / Play Console
                acceptance surface. This section is additive and does
                not alter the locked Contact editorial (heading,
                intro, form, primary/additional email columns). It
                names the CrewZNatioN mobile application explicitly,
                lists the support categories reviewers look for, and
                provides voluntary-diagnostic and security guidance
                required for account/data/deletion requests. */}
            <section
                className="px-6 md:px-12 lg:px-20 pb-32 max-w-6xl mx-auto"
                data-testid={isAr ? "app-support-section-ar" : "app-support-section"}
            >
                <div className="border-t border-white/10 pt-16">
                    <div className={`flex items-center gap-3 mb-5 ${isAr ? "justify-end" : ""}`}>
                        <span className="gold-rule" />
                        <p className={`overline ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}>
                            {isAr ? "دعم تطبيق CrewZNatioN" : "CrewZNatioN app support"}
                        </p>
                    </div>
                    <h2
                        className={`${isAr ? "font-arabic text-3xl md:text-4xl !leading-[1.3]" : "font-display text-3xl md:text-4xl leading-[1.05]"} text-[var(--crewz-text)] max-w-3xl`}
                        data-testid={isAr ? "app-support-heading-ar" : "app-support-heading"}
                    >
                        {isAr
                            ? "دعم تطبيق CrewZNatioN لأصحاب الحسابات."
                            : <>Support for members of the <Brand /> mobile application.</>}
                    </h2>
                    <p
                        className={`${isAr ? "font-arabic text-base md:text-lg leading-[1.95]" : "font-body text-base md:text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85 mt-6 max-w-3xl`}
                    >
                        {isAr ? (
                            <>يخدم هذا القسم أعضاء تطبيق <Brand /> على iOS و Android، إضافةً إلى زوّار الموقع. يمكن استخدام النموذج أعلاه أو الكتابة مباشرةً إلى أحد العناوين البريدية المذكورة، مع تحديد الفئة الأنسب لطلبك.</>
                        ) : (
                            <>This section serves members of the <Brand /> app on iOS and Android as well as website visitors. Use the form above, or write directly to one of the email addresses listed, and identify the category that best matches the request.</>
                        )}
                    </p>

                    {/* Categories */}
                    <div className={`mt-12 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-6 ${isAr ? "text-right" : ""}`}>
                        {(isAr
                            ? [
                                  { t: "الوصول إلى الحساب", d: "تسجيل الدخول، وإعادة تعيين كلمة المرور، ورموز التحقّق، وقفل الحساب." },
                                  { t: "طلبات الخصوصية والبيانات", d: "الوصول إلى البيانات، وتصحيحها، وسحب الموافقة وفق نظام حماية البيانات الشخصية." },
                                  { t: "حذف الحساب", d: "خطوات الحذف من داخل التطبيق أو الطلب الكتابي؛ التفاصيل الكاملة في صفحة حذف الحساب." },
                                  { t: "السلامة والإبلاغ", d: "الإبلاغ عن محتوىً، أو مضايقة، أو انتحال، أو مخاوف تخصّ سلامة العضو." },
                                  { t: "الفعاليات والتصاريح", d: "الطلبات، والدعوات، وتأكيد الحضور، ورموز QR، والحضور، ومسائل الدخول عند البوابة." },
                                  { t: "الجراج والمركبات", d: "طلبات صاحب المركبة، ورفع الوسائط، وإعدادات خصوصية الجراج، وحقوق النشر." },
                                  { t: "الهَب/السوق", d: "الإعلانات، والتحرير، والحذف، وسلامة المشترين، والبلاغات." },
                                  { t: "مشكلة تقنية", d: "الأعطال، ورسائل الخطأ، ومشكلات المزامنة، وتوافق إصدار التطبيق." },
                              ]
                            : [
                                  { t: "Account access", d: "Sign-in, password reset, verification codes, locked account." },
                                  { t: "Privacy & data request", d: "Access, correction, and consent withdrawal under the Saudi PDPL." },
                                  { t: "Account deletion", d: "In-app deletion or written request — full detail on the Account Deletion page." },
                                  { t: "Safety & reporting", d: "Reporting content, harassment, impersonation, or safety concerns about a member." },
                                  { t: "Events & passes", d: "Applications, invitations, RSVP, QR passes, check-in, and entrance questions." },
                                  { t: "Garage & vehicle", d: "Vehicle-owner applications, media uploads, Garage privacy settings, upload-rights." },
                                  { t: "Hub / Marketplace", d: "Listings, edits, removals, buyer-safety and reports." },
                                  { t: "Technical issue", d: "Crashes, error messages, sync issues, and app-version compatibility." },
                              ]
                        ).map((cat, i) => (
                            <div key={i} data-testid={`app-support-category-${i}`}>
                                <p className={`${isAr ? "font-arabic text-lg" : "font-display text-lg"} text-[var(--crewz-text)] font-semibold`}>
                                    {cat.t}
                                </p>
                                <p className={`${isAr ? "font-arabic text-sm leading-[1.9]" : "font-body text-sm leading-relaxed font-light"} text-[var(--crewz-text)]/70 mt-1`}>
                                    {cat.d}
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Voluntary diagnostics */}
                    <div className="mt-14 max-w-3xl">
                        <p className={`overline mb-4 text-zinc-500 ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px] text-right" : ""}`}>
                            {isAr ? "معلومات تشخيصية يمكن إرفاقها اختياريًا" : "Voluntary diagnostic details"}
                        </p>
                        <p className={`${isAr ? "font-arabic text-base leading-[1.95]" : "font-body text-base md:text-lg leading-relaxed font-light"} text-[var(--crewz-text)]/85`}>
                            {isAr
                                ? "لتسريع المعالجة، يمكنك إرفاق ما يلي في رسالتك:"
                                : "To help support respond faster, you may voluntarily include:"}
                        </p>
                        <ul
                            className={`${isAr ? "font-arabic text-base leading-[1.95] pr-5 [list-style:disc]" : "font-body text-base leading-relaxed font-light pl-5 list-disc"} text-[var(--crewz-text)]/85 mt-3 space-y-2 marker:text-[var(--crewz-gold)]`}
                        >
                            {(isAr
                                ? [
                                      "اسم المستخدم أو البريد المرتبط بحساب CrewZNatioN.",
                                      "نوع الجهاز ونظام التشغيل وإصدار التطبيق (إن أمكن).",
                                      "وصف واضح لما حدث والوقت التقريبي.",
                                      "لقطات شاشة أو رسائل خطأ لا تكشف عن بيانات حسّاسة.",
                                  ]
                                : [
                                      "the username or email registered on the CrewZNatioN account,",
                                      "device model, operating system version, and app build (where known),",
                                      "a clear description of what happened and the approximate time,",
                                      "screenshots or error messages that do not reveal sensitive data.",
                                  ]
                            ).map((li, i) => (
                                <li key={i}>{li}</li>
                            ))}
                        </ul>
                    </div>

                    {/* Security notice */}
                    <div
                        className={`mt-10 max-w-3xl border-l-2 ${isAr ? "border-r-2 border-l-0 pr-5 pl-0 text-right" : "pl-5"} border-[var(--crewz-gold)] py-2`}
                        data-testid={isAr ? "app-support-security-ar" : "app-support-security"}
                    >
                        <div className={`inline-flex items-center gap-2 ${isAr ? "flex-row-reverse" : ""}`}>
                            <ShieldAlert size={16} className="text-[var(--crewz-gold)] shrink-0" />
                            <p className={`overline text-zinc-400 ${isAr ? "font-arabic !text-[20.04px] md:!text-[21.8px]" : ""}`}>
                                {isAr ? "لا ترسل هذه البيانات مطلقًا" : "Never send this information"}
                            </p>
                        </div>
                        <p className={`${isAr ? "font-arabic text-base leading-[1.95] mt-3" : "font-body text-base leading-relaxed font-light mt-3"} text-[var(--crewz-text)]/85`}>
                            {isAr
                                ? "لن تطلب CrewZNatioN منك عبر البريد الإلكتروني كلمة المرور، أو رموز التحقّق لمرة واحدة (OTP)، أو أرقام بطاقات الدفع الكاملة، أو أرقام الهوية الوطنية، أو مستندات حسّاسة أخرى. أي رسالة تصل تحتوي على مثل هذه البيانات ستُعالَج ويُحذَف المحتوى الحسّاس من سجلّات الدعم."
                                : "CrewZNatioN will never ask by email for your password, one-time verification codes (OTP), full payment-card numbers, national identification numbers, or other sensitive documents. Any message that includes such information will be handled and the sensitive content will be removed from support records."}
                        </p>
                    </div>

                    {/* Cross-links */}
                    <div className={`mt-10 flex flex-wrap gap-x-6 gap-y-2 ${isAr ? "justify-end" : ""}`}>
                        <Link
                            to={isAr ? "/ar/privacy" : "/privacy"}
                            className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                            data-testid={isAr ? "app-support-privacy-link-ar" : "app-support-privacy-link"}
                        >
                            {isAr ? "سياسة الخصوصية" : "Privacy Policy"}
                        </Link>
                        <Link
                            to={isAr ? "/ar/account-deletion" : "/account-deletion"}
                            className="text-[var(--crewz-gold)] hover:opacity-80 transition-opacity underline-offset-4 hover:underline text-base"
                            data-testid={isAr ? "app-support-deletion-link-ar" : "app-support-deletion-link"}
                        >
                            {isAr ? "حذف الحساب" : "Account Deletion"}
                        </Link>
                    </div>
                </div>
            </section>

            <PageFooter />
        </div>
    );
}
